// src/utils/isGibberish.js
import { remove as removeDiacritics } from 'diacritics';

/**
 * Detects if a text input is gibberish based on patterns and heuristics.
 * @param {string} input - The user input to evaluate.
 * @param {string} locale - The current language (e.g., "en", "es", "fr").
 * @returns {boolean} - True if gibberish is detected, otherwise false.
 */
export function isGibberish(input, locale = 'en') {
  if (!input || typeof input !== 'string') return true;

  // Optionally remove diacritics based on locale
  const preserveDiacriticsLanguages = ['es', 'fr', 'pt', 'pl', 'hu', 'vi'];
  const normalized = preserveDiacriticsLanguages.includes(locale)
    ? input.trim().toLowerCase()
    : removeDiacritics(input.trim().toLowerCase());

  // Basic gibberish heuristics:
  const excessiveConsonants = /[bcdfghjklmnpqrstvwxyz]{5,}/i;
  const excessiveVowels = /[aeiou]{5,}/i;
  const repeatedLetters = /(.)\1{3,}/;  // e.g. "aaaaa", "ssss"
  const symbolsOnly = /^[^a-zA-Z0-9À-ÿ]+$/;
  const shortNonword = /^[^a-zA-ZÀ-ÿ]{1,2}$/;
  const noRecognizableWords = normalized.split(' ').every(word => word.length < 2);

  return (
    excessiveConsonants.test(normalized) ||
    excessiveVowels.test(normalized) ||
    repeatedLetters.test(normalized) ||
    symbolsOnly.test(normalized) ||
    shortNonword.test(normalized) ||
    noRecognizableWords
  );
}